import {AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import {DictionaryBaseDto} from '../../model/dtos';
import {DictionaryProfile} from '../../model/dictionary-ids';

const DICT_RADIO_ROW_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DictRadioRowComponent),
  multi: true,
};

@Component({
  selector: 'dict-radio-row',
  template: `
    <div class="bon-row">
      <label class="bon-label">{{ labelKey ? (labelKey | translate) : label }}</label>
      <dict-radio
        [dictionary]="dictionary"
        [nullLabel]="nullLabel"
        [label]="itemLabel"
        (changeItem)="onChangeItem($event)"
        [parentDictionary]="parentDictionary"
        [parentDictionaryEntryId]="parentDictionaryEntryId"
        [name]="name"
        [disabled]="disabled"
        [codeRegexp]="codeRegexp"
        [ngModel]="value"
        [hiddenIds]="hiddenIds"
        [entries]="entries"
        [buId]="buId"
        [required]="required"
        [chooseRequired]="chooseRequired"
        [ngClass]="getCssClasses(control)"
        [profileId]="profileId"
        [unselect]="unselect"
        [externalTemplate]="template"
        [inline]="inline"
      >
      </dict-radio>
      <error-message [control]="control" [show]="showErrors"></error-message>
    </div>
  `,
  styles: ['label.bon-label { vertical-align: top !important }'],
  providers: [DICT_RADIO_ROW_CONTROL_VALUE_ACCESSOR],
})
export class DictRadioRowComponent implements ControlValueAccessor, AfterViewInit {
  @Input() name: string;

  @Input() control: AbstractControl;
  @Input() label: string;
  @Input() labelKey: string;
  @Input() disabled: any;
  @Input() showErrors: boolean;

  @Input() errorClass = '';
  @Input() nullLabel: string;
  @Input() dictionary: string;
  @Input() parentDictionary: string;
  @Input() parentDictionaryEntryId: number;
  @Input() itemLabel: 'name' | 'code' | 'codeAndName' = 'name';
  @Input() codeRegexp: RegExp;

  @Input() buId: number;
  @Input() profileId: DictionaryProfile;

  // can be provided instead of dictionary
  @Input() entries: DictionaryBaseDto[];

  @Input() required: boolean;
  @Input() chooseRequired: boolean;
  @Input() unselect = false;

  @Input() inline = true;

  @ContentChild(TemplateRef) template: TemplateRef<any>;

  _hiddenIds: Set<number>;

  @Input() set hiddenIds(hiddenIds: Set<number>) {
    this._hiddenIds = hiddenIds;
  }

  get hiddenIds() {
    return this._hiddenIds;
  }

  @Output() changeItem = new EventEmitter<DictionaryBaseDto>();

  private onChangeListeners: Function;
  private onTouchedListeners: Function;
  public value: DictionaryBaseDto;

  constructor(private injector: Injector) {}

  public getCssClasses(control: AbstractControl) {
    const cssClasses: any = {};
    cssClasses['bon-input-size'] = true;
    if (control && control.invalid && this.showErrors) {
      cssClasses[this.errorClass] = true;
    }
    return cssClasses;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChangeListeners = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedListeners = fn;
  }

  onChangeItem(event: DictionaryBaseDto) {
    this.value = event;
    this.onChangeListeners(this.value);
    this.changeItem.emit(event);
  }

  /**
   * Needed to be able to disable model-validated components. Such components must be disabled in FormGroup definition:
   *
   *  Example:
   * form = new FormGroup({
   *     first: new FormControl({value: 'Nancy', disabled: true}, Validators.required),
   *     last: new FormControl('Drew', Validators.required)
   *   });
   *
   * @param disabled
   */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  ngAfterViewInit(): void {
    if (!this.control) {
      this.control = this.injector.get(NgControl, null).control;
    }
  }
}
